import axios from "axios";
import { API } from "../../Services/api-service";

// Constants
const BOX_CONSTRAINTS = {
  MIN_WIDTH: 5,
  MIN_HEIGHT: 3,
};

const BOX_TYPES = {
  select: "multi-select",
  "select-one": "single-select",
  latex: "math",
  "@descriptive": "descriptive",
};
const MIN_BOX_WIDTH = 3;
const MIN_BOX_HEIGHT = 2;
const COPY_OFFSET = 2;
const MAX_POSITION = 95;

// Utility functions
export const getRelativeCoordinates = (event, containerRect) => {
  const containerHeight = containerRect.height;
  const scrollTop = containerRect.scrollTop || 0;

  const x = ((event.clientX - containerRect.left) / containerRect.width) * 100;
  const y =
    ((event.clientY - containerRect.top + scrollTop) / containerHeight) * 100;

  return { x, y };
};

export const calculateBoxDimensions = (startCoords, currentCoords) => {
  const width = Math.abs(currentCoords.x - startCoords.x);
  const height = Math.abs(currentCoords.y - startCoords.y);
  const x = Math.min(startCoords.x, currentCoords.x);
  const y = Math.min(startCoords.y, currentCoords.y);

  return {
    x: Math.max(0, Math.min(100 - width, x)),
    y: Math.max(0, Math.min(100 - height, y)),
    width: Math.min(width, 100 - x),
    height: Math.min(height, 100 - y),
  };
};

export const isBoxLargeEnough = (dimensions) => {
  return dimensions.width > MIN_BOX_WIDTH && dimensions.height > MIN_BOX_HEIGHT;
};

export const createNewBox = (dimensions, type = "text") => ({
  id: `box-${Date.now()}`,
  boxCoords: dimensions,
  type,
  answer: "",
});

export const createCopiedBox = (originalBox) => ({
  ...originalBox,
  id: `div-${Date.now()}`,
  boxCoords: {
    ...originalBox.boxCoords,
    x: Math.min(MAX_POSITION, originalBox.boxCoords.x + COPY_OFFSET),
    y: Math.min(MAX_POSITION, originalBox.boxCoords.y + COPY_OFFSET),
  },
  answer: "",
});

export const calculateNewPosition = (initialCoords, deltaX, deltaY) => ({
  ...initialCoords,
  x: Math.max(0, Math.min(100 - initialCoords.width, initialCoords.x + deltaX)),
  y: Math.max(
    0,
    Math.min(100 - initialCoords.height, initialCoords.y + deltaY)
  ),
});

export const calculateNewSize = (initialCoords, deltaX, deltaY) => ({
  ...initialCoords,
  width: Math.max(
    BOX_CONSTRAINTS.MIN_WIDTH,
    Math.min(100 - initialCoords.x, initialCoords.width + deltaX)
  ),
  height: Math.max(
    BOX_CONSTRAINTS.MIN_HEIGHT,
    Math.min(100 - initialCoords.y, initialCoords.height + deltaY)
  ),
});

export const getBoxData = (exercise, isAdmin = false, showAnswers = false) => {
  const { properties } = exercise || {};
  const userSubmittedAnswers = exercise?.answers
    ? JSON.parse(exercise.answers)
    : null;

  const isExerciseAttempted =
    exercise?.answers !== null &&
    exercise?.submission?.attemptedAll === 1 &&
    Object.keys(exercise?.answers || {}).length > 0;

  const boxes = Object.entries(properties?.answers || {}).map(
    ([key, value], index, arr) => {
      const [, x, y, width, height] = key.split("-").map(parseFloat);

      let variations = [];

      const { id, type, correctAnswer } = parseAnswer(value, key);

      if (type === "text") {
        variations = Object.entries(properties?.ansVariations || {})
          .filter(([variationKey]) => variationKey === key)
          .map(([, variationValue]) => variationValue);

        variations = flattenArray(variations);
      }

      const userSubmittedAnswer = parseAnswer(
        userSubmittedAnswers?.[key],
        key
      ).correctAnswer;

      return {
        id: `box-${index + 1}-${key}`,
        key,
        questionId: id || `box-${index + 1}-${key}`,
        boxCoords: { x, y, width, height },
        type: properties?.isLatex?.[key] ? "math" : type,
        userSubmittedAnswer,
        answer: isAdmin
          ? correctAnswer
          : (!isExerciseAttempted &&
              exercise?.submission?.attemptedAll === 1) ||
            exercise?.hasDraft
          ? userSubmittedAnswer
          : null,
        lastAnswer: isAdmin ? correctAnswer : null,
        correctAnswer,
        variations,
        isAnswerCorrect: exercise?.submission?.correctAnswers?.[key],
      };
      // lastAnswer field is added for the purpose of saving draft answer
      // if answer and lastAnswer are same, then it means no change in answer so we can skip saving it
    }
  );

  if (showAnswers) {
    return boxes.map((box) =>
      findCorrectAnswer(box, showAnswers, exercise?.submission?.correctAnswers)
    );
  }

  return boxes;
};

const parseAnswer = (answerString, key) => {
  if (
    answerString === true ||
    answerString === false ||
    answerString === undefined ||
    answerString === null ||
    answerString?.includes(":undefined")
  ) {
    return {
      correctAnswer: null,
    };
  }
  let id = "";
  let type = "text";
  let correctAnswer = answerString;
  const parts = answerString?.split(":");
  if (
    parts?.length > 1 &&
    Object.keys(BOX_TYPES).some((type) => answerString.startsWith(`${type}:`))
  ) {
    const [rawType, rawAnswer] = parts || [];
    type = BOX_TYPES[rawType] || "text";

    if (type === "single-select" || type === "multi-select") {
      correctAnswer = rawAnswer === "yes";
      id = parts[2] || key;
    } else {
      correctAnswer = answerString.replace("latex:", "");
      id = key;
    }
  } else if (correctAnswer === "@descriptive") {
    type = "descriptive";
    correctAnswer = "";
  }

  return {
    id,
    type,
    correctAnswer,
  };
};

export const findCorrectAnswer = (box, showAnswers) => {
  let { type, correctAnswer, userSubmittedAnswer, variations } = box;

  let isAnswerCorrect = false;

  if (["single-select", "multi-select"].includes(type)) {
    if (type === "single-select") {
      if (correctAnswer) {
        isAnswerCorrect = userSubmittedAnswer ? true : null;
      } else {
        isAnswerCorrect = userSubmittedAnswer ? false : null;
      }
    }
    if (type === "multi-select") {
      if (correctAnswer) {
        isAnswerCorrect = userSubmittedAnswer ? true : "unselected";
      } else {
        isAnswerCorrect = userSubmittedAnswer ? false : null;
      }
    }
    if (showAnswers) {
      isAnswerCorrect = correctAnswer;
    }
  } else if (type === "text") {
    isAnswerCorrect = [correctAnswer?.toLowerCase(), ...variations].includes(
      userSubmittedAnswer?.toLowerCase()
    );
  } else if (type === "math") {
    isAnswerCorrect = correctAnswer === userSubmittedAnswer;
  } else if (type === "descriptive") {
    isAnswerCorrect = "unselected";
  }

  return { ...box, isAnswerCorrect };
};

export const getChangedAnswers = (boxes) => {
  const types = {
    "multi-select": "select:",
    "single-select": "select-one:",
    math: "",
    text: "",
    descriptive: "",
  };
  const updatedAnswers = {};
  const allAnswers = {};
  const questionIdSet = new Set();

  const rawAnswer = {};

  boxes.forEach((box) => {
    const { key, answer } = getfinalAnswerData(box, types);

    const isUserSubmittedAnswerAvailable =
      box.userSubmittedAnswer !== null &&
      box.userSubmittedAnswer !== undefined &&
      box.userSubmittedAnswer !== "";

    const isAnswerAvailable = box.answer !== null && box.answer !== "";

    if (isUserSubmittedAnswerAvailable || isAnswerAvailable) {
      allAnswers[key] = isAnswerAvailable ? answer : box.userSubmittedAnswer;
    }

    if (isAnswerAvailable) {
      updatedAnswers[key] = answer;
      if (["single-select", "multi-select"].includes(box.type)) {
        questionIdSet.add(box.questionId);
      } else {
        questionIdSet.add(box.id);
      }
    }

    if (["single-select", "multi-select"].includes(box.type)) {
      rawAnswer[key] = isAnswerAvailable
        ? answer
        : `${types[box.type]}${box.userSubmittedAnswer ? "yes" : "no"}:${
            box.questionId
          }`;
    } else {
      rawAnswer[key] = isAnswerAvailable ? answer : box.userSubmittedAnswer;
    }
  });

  return {
    updatedAnswers,
    allAnswers,
    allAttempted: isAllSelectAttempted(boxes, rawAnswer),
    length: questionIdSet.size,
  };
};

const isAllSelectAttempted = (boxes, changedAnswers) => {
  const selectQuestions = {};

  boxes.forEach((box) => {
    if (
      box.key in changedAnswers &&
      ["single-select", "multi-select"].includes(box.type)
    ) {
      if (!selectQuestions[box.questionId]) {
        const answer = changedAnswers[box.key];
        selectQuestions[box.questionId] = answer
          ? answer?.includes(":yes:")
          : false;
      }
    }
  });

  return Object.values(selectQuestions).every((value) => value === true);
};

const getfinalAnswerData = (box, types) => {
  const { type, answer, questionId, key } = box;

  let finalAnswer = `${types[type]}`;

  if (["single-select", "multi-select"].includes(type)) {
    finalAnswer += `${answer ? "yes" : "no"}:${questionId}`;
  } else {
    finalAnswer += answer;
  }

  return {
    key: key,
    answer: box.answer === null ? null : finalAnswer,
  };
};

export const saveDraft = (box, boxes, exersiceDetails) => {
  if (
    !exersiceDetails ||
    !exersiceDetails?.exerciseId ||
    !exersiceDetails?.pageId
  ) {
    return;
  }

  if (
    box.answer !== null &&
    box.answer !== "" &&
    box.answer !== box.lastAnswer
  ) {
    const { allAnswers } = getChangedAnswers(boxes);

    API.post(
      `/exercises/${exersiceDetails?.exerciseId}/page/${exersiceDetails?.pageId}/draft`,
      {
        answers: JSON.stringify(allAnswers),
      }
    ).catch((err) => {
      console.error("Error saving draft", err);
    });
  }
};

function flattenArray(arr) {
  return arr.reduce((flat, item) => {
    return flat.concat(Array.isArray(item) ? flattenArray(item) : item);
  }, []);
}

// functions to extract box dimensions and position from the html content
// reason to add this function: Some of the box coordinate and dimension data is corrupted
// for some of the old math worksheet exercises which is stored in the database but the
// admin side html content stores the correct data. This function extract the correct
// values for top, left, width and height from the html content stored in s3
export const getHtmlBoxesData = async (url, setBoxes, retryCount = 2) => {
  try {
    const response = await axios.get(url);

    if (response.status !== 200) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const htmlString = response.data;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const parserError = doc.querySelector("parsererror");
    if (parserError) {
      throw new Error("HTML parsing failed: " + parserError.textContent);
    }

    const canvas = doc.getElementById("canvas");

    if (!canvas) {
      setBoxes([]);
      return [];
    }

    const boxes = canvas.querySelectorAll("[data-ans][id][type]");

    const boxesData = {};

    const parseStyles = (box, property) => {
      return parseFloat(
        box?.style?.[property].replace("%", "").replace("px", "")
      );
    };

    const parseId = (styleObj) => {
      return `div-${styleObj.x.toFixed(3)}-${styleObj.y.toFixed(3)}-${styleObj.width.toFixed(3)}-${styleObj.height.toFixed(3)}`;
    };

    Array.from(boxes).forEach((box) => {
      const styleValues = {
        width: parseStyles(box, "width"),
        height: parseStyles(box, "height"),
        x: parseStyles(box, "left"),
        y: parseStyles(box, "top"),
      };

      const [, x, y, width, height] = box?.id?.split("-").map(parseFloat);

      const oldStyleValues = {
        x,
        y,
        width,
        height,
      };

      const newId = parseId(styleValues);
      const oldId = parseId(oldStyleValues);

      boxesData[box.id] = {
        id: box.id,
        newId: `div-${styleValues.x}-${styleValues.y}-${styleValues.width}-${styleValues.height}`,
        isCorrupted: newId !== oldId,
        type: box.getAttribute("type"),
        dataAns: box.getAttribute("data-ans"),
        style: styleValues,
      };
    });

    setBoxes(boxesData);
    return boxesData;
  } catch (error) {
    if (retryCount > 0) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return getHtmlBoxesData(url, setBoxes, retryCount - 1);
    }

    setBoxes([]);
    return [];
  }
};
